import type { Base } from "./base";
import type { MessageMedia } from "./message";
import type { MessageTemplateEmailDoc } from "./message-template";

export interface Automation extends Base {
  org: string;
  gym: string;
  name: string;
  active: boolean;
  trigger: {
    type: AutomationTrigger;
    conditions: AutomationCondition[];
  };
  actions: AutomationAction[];
}

export interface AutomationTrigger {
  type: AutomationTriggerType;
  conditions: AutomationCondition[];
  delay?: {
    interval: "day" | "hour" | "minute";
    value: number;
  };
}

export const AutomationTriggerType = {
  _TRIGGER_PLACEHOLDER: "trigger_placeholder",
  incomingMessage: "incoming_message",
  formSubmission: "form_submission",
  addedToList: "added_to_list",
  removedFromList: "removed_from_list",
  customerEvent: "customer_event",
} as const;

export type AutomationTriggerType =
  (typeof AutomationTriggerType)[keyof typeof AutomationTriggerType];

export interface AutomationAction {
  type: AutomationActionType;
  options: Record<string, unknown>;
}

export type MessageProtocolType = "sms" | "email";

/**
 * Time window for a single day. Mirrors the API `DeliveryWindowDay` model.
 */
export interface DeliveryWindowDay {
  startTime: string; // "HH:MM" 24-hour, e.g. "08:00"
  endTime: string; // "HH:MM" 24-hour, e.g. "20:00"
}

/**
 * Delivery window for a send-message action. Mirrors the API `DeliveryWindow`
 * model; constrains when a flow's messages can be delivered. A `null` day means
 * no delivery is allowed that day.
 */
export interface DeliveryWindow {
  enabled: boolean;
  timeZone: string; // IANA timezone, e.g. "America/New_York"
  monday: DeliveryWindowDay | null;
  tuesday: DeliveryWindowDay | null;
  wednesday: DeliveryWindowDay | null;
  thursday: DeliveryWindowDay | null;
  friday: DeliveryWindowDay | null;
  saturday: DeliveryWindowDay | null;
  sunday: DeliveryWindowDay | null;
}

/**
 * Email payload snapshotted onto a send-message action. Mirrors the API
 * `automationActionEmail` zod schema; the server renders `htmlBody` from
 * `templateDoc` at send time.
 *
 * When `to` is set, the flow sends to that address instead of the contact's
 * email (used for internal notifications and similar).
 */
export interface AutomationActionEmail {
  htmlBody?: string;
  templateId?: string;
  templateVersion?: number;
  templateName?: string;
  templateSubject?: string;
  templatePreviewText?: string;
  templateDoc?: MessageTemplateEmailDoc;
  to?: {
    name?: string;
    email: string;
  };
  replyTo?: string;
}

/** Typed view of an `outgoing_message` action's loosely-typed options. */
export interface SendMessageActionOptions {
  body?: string;
  template?: string;
  protocol?: MessageProtocolType;
  subject?: string;
  email?: AutomationActionEmail;
  deliveryWindow?: DeliveryWindow;
  /** MMS attachments (images, vCards). Template mode inherits template media when omitted. */
  media?: MessageMedia[];
}

/** Typed view of a `create_task` action's options. */
export interface CreateTaskActionOptions {
  title: string;
  description?: string;
  /** Assignee user id. */
  user?: string;
  tags?: string[];
  due?: {
    interval: "day" | "hour" | "minute";
    value: number;
  };
}

/**
 * Typed view of an `outgoing_message_multichannel` action's options. Both the
 * text and email variants are required; the server texts the contact when it
 * can, emails them otherwise, and creates `fallbackTask` (when configured) if
 * neither channel is reachable.
 */
export interface SendMessageMultichannelActionOptions {
  sms: {
    body: string;
    media?: MessageMedia[];
  };
  email: {
    subject: string;
    previewText?: string;
    body: string;
  };
  deliveryWindow?: DeliveryWindow;
  fallbackTask?: CreateTaskActionOptions;
}

export interface AutomationCondition {
  key: string;
  operators: AutomationConditionOperator[];
}

export interface AutomationConditionOperator {
  op: "$eq" | "$ne" | "$in" | "$nin" | "$exists" | "$gt" | "$gte" | "$lt" | "$lte" | "$regex";
  value:
    | string
    | number
    | boolean
    | Date
    | string[]
    | number[]
    | boolean[]
    | Date[]
    | Record<string, unknown>;
}

export const AutomationActionType = {
  _ACTION_PLACEHOLDER: "action_placeholder",
  sendMessage: "outgoing_message",
  sendMessageMultichannel: "outgoing_message_multichannel",
  addToList: "add_to_list",
  removeFromList: "remove_from_list",
  webhook: "webhook",
  timer: "timer",
  addTags: "add_tags",
  removeTags: "remove_tags",
  createTask: "create_task",
  // Not offered in the editor palette; retained so script-created flows render.
  setCustomerProperty: "set_customer_property",
} as const;

export type AutomationActionType = (typeof AutomationActionType)[keyof typeof AutomationActionType];
